import * as Sentry from '@sentry/react';
import { useCallback, useState, type ReactNode } from 'react';

import Button from '@modules/common/components/button';
import Modal from '@modules/common/components/modal';
import ConfirmModalTabs from './confirm-modal-tabs';

export interface ConfirmModalTab {
  title: string;
  content: ReactNode;
  onSkip?: () => void;
  onConfirm: () => Promise<void>;
  // E2E:套用到此 tab 共用的 skip / confirm button data-testid。
  // 例:`confirm-modal-deposit` → `confirm-modal-deposit-{skip,confirm}-button`。
  testIdPrefix?: string;
}

interface ConfirmModalProps {
  tabs: ConfirmModalTab[];
  isModalOpen: boolean;
  onClose: (confirmed?: boolean) => void;
}

const ConfirmModal = ({ tabs, isModalOpen, onClose }: ConfirmModalProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isConfirming, setIsConfirming] = useState(false);

  const nextStep = () => setCurrentStep((prev) => prev + 1);
  const prevStep = () => setCurrentStep((prev) => prev - 1);

  const currentTab = tabs[currentStep];
  const currentOnSkip = currentTab?.onSkip;

  const handleNext = useCallback(() => {
    currentOnSkip?.();
    nextStep();
  }, [currentOnSkip]);

  const handleClose = useCallback(() => {
    setCurrentStep(0);
    onClose();
  }, [onClose]);

  const hasNext = currentStep + 1 < tabs.length;

  const handleAgree = async () => {
    if (!currentTab) return;

    setIsConfirming(true);
    try {
      await currentTab.onConfirm();
      if (hasNext) {
        nextStep();
      } else {
        setCurrentStep(0);
        onClose(true);
      }
    } catch (error) {
      // 錯誤提示由 onConfirm 內部處理（例如 useUpdateCustomer 的 onError toast），
      // 這裡保持 modal 開啟讓使用者重試，並回報 Sentry
      // （api client interceptor 只回報 5xx／網路錯誤，4xx 與非 API 例外需在此補上）
      Sentry.captureException(error, {
        tags: { component: 'confirm-modal' },
        extra: { tabTitle: currentTab.title },
      });
    } finally {
      setIsConfirming(false);
    }
  };

  if (tabs.length === 0) {
    return null;
  }

  return (
    <Modal
      isOpen={isModalOpen}
      close={handleClose}
      autoClose={false}
      panelClassName="bg-white-10 max-w-[1000px] min-w-[300px] h-fit md:max-h-[1200px]"
    >
      <Modal.Title> </Modal.Title>
      <ConfirmModalTabs showPrev={currentStep > 0} onClickPrev={prevStep}>
        {tabs.map(({ title }, index) => (
          <ConfirmModalTabs.Item key={title} isActive={index <= currentStep}>
            {title}
          </ConfirmModalTabs.Item>
        ))}
      </ConfirmModalTabs>
      <div className="mb-4 h-[600px] overflow-auto px-6 py-10 md:h-fit">
        {currentTab?.content}
      </div>
      <Modal.Footer>
        {hasNext && (
          <Button
            onClick={handleNext}
            variant="outlined"
            className="px-4 py-2"
            disabled={isConfirming}
            data-testid={
              currentTab?.testIdPrefix
                ? `${currentTab.testIdPrefix}-skip-button`
                : undefined
            }
          >
            查看下一則
          </Button>
        )}

        <Button
          onClick={handleAgree}
          disabled={isConfirming}
          data-testid={
            currentTab?.testIdPrefix
              ? `${currentTab.testIdPrefix}-confirm-button`
              : undefined
          }
        >
          {isConfirming ? '處理中...' : '我已閱讀並同意'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ConfirmModal;
